import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ReactComponent as BackIcon } from "../../assets/icons/back.svg";
import { useNavigate } from "react-router-dom";
import { getTicketById } from "../../app/api/admin/tickets";
import { toast } from "react-toastify";
import { updateStatus } from "../../app/api/admin/tickets";
import "react-toastify/dist/ReactToastify.css";
import Modal from "../Common/Modal";
import {
  formatDateTimeToReadable,
  formatDateWithTime,
} from "../../utils/commonUtils";
import NoTicketFound from "./NoTicketFound";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { AxiosError } from "axios";
import { string } from "yup";
import ReportModal from "./ReportModal";

interface Ticket {
  _id: string;
  image: string;
  source: string;
  issueType: string;
  liveSince: string;
  createdBy: string;
  priority: string;
  reason: string;
  email: string;
  status: string;
  postId: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  ticketMarked: string;
  productType?: string;
  stylingDetails?: string;
  errorType?: string;
  __v: number;
}

type Props = {
  isSidebarOpen: boolean;
};

const ReviewReport: React.FC<Props> = ({ isSidebarOpen }) => {
  const { id } = useParams();
  const [currentData, setCurrentData] = useState<Ticket | null>(null);
  const [error, setError] = useState<boolean>(false);
  const navigate = useNavigate();
  const [actionClicked, setActionClicked] = useState<boolean>(false);
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const handleNavigate = (): void => {
    navigate("/tickets");
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      if (id) {
        try {
          const ticketData = await getTicketById(id);
          setCurrentData(ticketData);
          if (ticketData?.status === "closed") {
            setActionClicked(true);
          }
          if (ticketData.ticketMarked === "invalid") {
            setSelectedAction("invalid");
          } else if (ticketData.ticketMarked === "removed") {
            setSelectedAction("remove");
          }
        } catch (error) {
          setError(true);
          console.error("Error fetching ticket data:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async () => {
    if (!selectedAction) {
      toast.error("Please select an action before submitting!", {
        hideProgressBar: true,
      });
      return;
    }
    setIsModalOpen(true);
  };

  const confirmAction = async () => {
    const adminAction = selectedAction === "remove";
    try {
      const responseStatus = await updateStatus({
        id: id as string,
        postId: currentData?.postId as string,
        adminAction: adminAction,
      });

      if (responseStatus === 200) {
        toast.success("Post updated successfully!", {
          hideProgressBar: true,
        });
        handleNavigate();
      } else {
        toast.error("Error updating post. Please try again.", {
          hideProgressBar: true,
        });
      }
    } catch (error) {
      if ((error as AxiosError)?.response?.status === 400) {
        toast.error("Error updating post. Please try again.", {
          hideProgressBar: true,
        });
      }
    }
    setIsModalOpen(false);
  };
  const generateFiveDigitId = (mongoId: string | undefined) => {
    if (!mongoId) return 0;
    const numericPart = parseInt(mongoId.slice(-5), 16); // Use the last 5 characters of the MongoDB ID
    return (numericPart % 90000) + 10000; // Ensure the result is a 5-digit number
  };

  if (error) {
    return <NoTicketFound />;
  }
  if (isLoading) {
    return (
      <div className="bg-white h-[calc(100vh-90px)] w-[calc(100vw-260px)] ml-1 fixed overflow-scroll p-6">
        <div
          className="w-8 h-8 flex justify-center items-center   cursor-pointer"
          onClick={handleNavigate}
        >
          <BackIcon />
        </div>
        <div className="flex mb-4">
          <div className="w-8 h-8 mr-4">
            <Skeleton circle width={32} height={32} />
          </div>
          <div className="flex-grow">
            <Skeleton width={200} height={24} />
          </div>
        </div>
        <div className="flex">
          <div className="w-[47%] mr-10">
            <Skeleton height={400} />
          </div>
          <div className="flex-grow space-y-6">
            {[
              "Source",
              "Live Since",
              "Description",
              "Nature of Issue",
              "Priority",
            ].map((label, index) => (
              <div key={index}>
                <Skeleton width={100} height={16} />
                <Skeleton height={40} />
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <Skeleton width={200} height={40} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white justify-around  transition-all duration-500 ease-in-out  h-[calc(100vh-90px)]  ${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"}  ml-1 fixed overflow-y-auto ${isModalOpen ? "z-10" : ""}`}
    >
      <div className="flex m-1 ">
        <div
          className="w-8 h-8 flex justify-center items-center   cursor-pointer"
          onClick={handleNavigate}
        >
          <BackIcon />
        </div>
        <div className="flex justify-center items-center pl-[3.7vw]">
          <p className="font-bold text-[18px] font-poppins">
            Ticket No -{" "}
            <span className="font-thin">#{generateFiveDigitId(id)}</span>
          </p>
        </div>
      </div>
      <div className="flex  pl-[6vw] ">
        {currentData && (
          <div className="flex  justify-center items-center w-[47%]  ">
            <img
              src={currentData.image}
              className="  rounded-lg object-cover"
            />
          </div>
        )}
        <div className="flex  w-full  pl-10 overflow-y-auto max-h-[calc(100vh-200px)] scroller">
          <div className="space-y-6 w-full pr-[6vw]">
            <div>
              <p className="text-sm font-poppins ">Source</p>
              <a
                href={currentData?.source}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray p-2 rounded-lg text-[14px] font-poppins block cursor-pointer"
              >
                {currentData?.source}
              </a>
            </div>
            <div>
              <p className="text-sm font-poppins">Live Since</p>
              <p className="bg-gray p-2 rounded-lg font-poppins text-[14px]">
                {formatDateTimeToReadable(currentData?.liveSince || "")}
              </p>
            </div>
            <div>
              <p className="text-sm font-poppins">Description</p>
              <p className="bg-gray p-2 rounded-lg h-20 overflow-y-auto font-poppins text-[14px]  ">
                {currentData?.reason}
              </p>
            </div>
            <div>
              <p className="text-sm font-poppins">Nature of Issue</p>
              <p className="bg-gray p-2 rounded-lg font-poppins text-[14px]">
                {currentData?.issueType}
              </p>
            </div>
            <div>
              <p className="text-sm font-poppins">Priority</p>
              <p className="bg-gray p-2 rounded-lg font-poppins text-[14px]">
                {currentData?.priority}
              </p>
            </div>
            <div>
              <p className="text-sm font-poppins">Product Type</p>
              <p className="bg-gray p-2 rounded-lg font-poppins text-[14px]">
                {currentData?.productType?.split("/")?.[0] || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm font-poppins">Trend Type</p>
              <p className="bg-gray p-2 rounded-lg font-poppins text-[14px]">
                {currentData?.stylingDetails || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm font-poppins">Error Type</p>
              <p className="bg-gray p-2 rounded-lg font-poppins text-[14px]">
                {currentData?.errorType || "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="pl-[6vw] flex space-x-5 font-poppins mt-2">
        <div className="flex flex-col font-bold ">
          <p>Issue Reported -</p>
          <p>Reported By -</p>
        </div>
        <div className="flex flex-col">
          <p>{formatDateWithTime(new Date(currentData?.createdAt || ""))}</p>
          <p>{currentData?.createdBy}</p>
        </div>
      </div>
      <div
        className={` font-bold text-center  font-poppins ${
          currentData?.status === "closed"
            ? "text-gray-400 cursor-not-allowed"
            : ""
        }`}
        onClick={() => {
          if (currentData?.status !== "closed") setActionClicked(prev => !prev);
        }}
      >
        <span className=" mt-2  cursor-pointer underline">Take Action</span>
      </div>
      {actionClicked && (
        <>
          <div className="flex mt-1 pl-32 justify-center align-center space-x-5 mb-2">
            <div className="flex space-x-2">
              <input
                type="radio"
                name="action"
                id="invalid"
                value="invalid"
                onChange={() => setSelectedAction("invalid")}
                checked={selectedAction === "invalid"}
                disabled={currentData?.status === "closed"}
                className={`font-poppins accent-black ${
                  currentData?.status === "closed" ? "text-gray-400" : ""
                }`}
              />
              <label
                htmlFor="invalid"
                className={`font-poppins text-[14px] accent-black ${
                  currentData?.status === "closed" ? "text-gray-400" : ""
                }`}
              >
                Ticket is invalid
              </label>
            </div>
            <div className="flex space-x-2">
              <input
                type="radio"
                name="action"
                id="remove"
                value="remove"
                onChange={() => setSelectedAction("remove")}
                disabled={currentData?.status === "closed"}
                checked={selectedAction === "remove"}
                className={`font-poppins  accent-black ${
                  currentData?.status === "closed" ? "text-gray-400" : ""
                }`}
              />
              <label
                htmlFor="remove"
                className={`font-poppins text-[14px] ${
                  currentData?.status === "closed" ? "text-gray-400" : ""
                }`}
              >
                Remove Content from platform
              </label>
            </div>
          </div>
          <div>
            <div className="flex justify-center items-center mt-1">
              <button
                className={`px-5 py-2 rounded-full font-poppins hover:bg-secondary ${
                  currentData?.status === "closed"
                    ? "bg-stone-400 text-white cursor-not-allowed"
                    : "bg-black text-white"
                }`}
                onClick={handleSubmit}
                disabled={currentData?.status === "closed"}
              >
                Submit
              </button>
            </div>
          </div>
        </>
      )}
      {isModalOpen && (
        <ReportModal
          closeModal={() => setIsModalOpen(false)}
          heading={
            selectedAction === "remove"
              ? "Remove the Content "
              : "Mark this ticket as Invalid"
          }
          confirmAction={confirmAction}
        />
      )}
      {/* <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Are you sure?"
      >
        <p>
          Do you really want to{" "}
          {selectedAction === "remove"
            ? "remove the content"
            : "mark this ticket as invalid"}
          ?
        </p>
        <div className="flex align-center justify-center gap-x-16 mt-4 font-poppins text-secondary font-semibold">
          <button
            className="px-12 py-2 bg-gray-300 rounded"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </button>
          <button
            className="px-12 py-2 bg-primary text-white font-poppins rounded-3xl "
            onClick={confirmAction}
          >
            Confirm
          </button>
        </div>
      </Modal> */}
    </div>
  );
};

export default ReviewReport;
